import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Anchor,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconCoins, IconPercentage, IconReceiptRefund, IconUser, IconSteeringWheel, IconReceiptOff } from "@tabler/icons-react";
import { CashbackStatus } from "@safiri/shared";
import { cashbackApi, type CashbackRecord, type CashbackTotals } from "../../api/cashback";
import type { Window } from "../../api/console";
import { usePermission } from "../../auth/usePermission";
import { DataTable, type Column } from "../common/DataTable";
import { QueryError } from "../common/QueryError";
import { KpiCard, KpiGroup } from "../console/KpiCard";
import { errorMessage } from "../../lib/axios";
import { formatCompact, formatDateTime, formatMoney, formatNumber, formatPercent } from "../../lib/format";
import { useI18n } from "../../i18n/context";
import { CashbackSettingsCard } from "./CashbackSettingsCard";

const PAGE_SIZE = 25;

/** Finance → Cashback: what the program paid, what it cost, reversals, fraud review, settings. */
export function CashbackPanel({ window }: { window: Window }) {
  const { t } = useI18n();
  const [view, setView] = useState<"report" | "pairs" | "settings">("report");

  return (
    <Stack>
      <SegmentedControl
        value={view}
        onChange={(v) => setView(v as typeof view)}
        data={[
          { value: "report", label: t("cb.view.report") },
          { value: "pairs", label: t("cb.view.pairs") },
          { value: "settings", label: t("cb.view.settings") },
        ]}
        style={{ alignSelf: "flex-start" }}
      />
      {view === "report" && <CashbackReport window={window} />}
      {view === "pairs" && <RepeatPairs window={window} />}
      {view === "settings" && <CashbackSettingsCard />}
    </Stack>
  );
}

function CashbackReport({ window }: { window: Window }) {
  const { t } = useI18n();
  const vsPrev = t("common.vsPrevious");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"all" | CashbackStatus>("all");
  const [reversing, setReversing] = useState<CashbackRecord | null>(null);
  const canManage = usePermission("finance.manage");

  const summary = useQuery({
    queryKey: ["cashback-summary", window.from, window.to, window.groupBy],
    queryFn: () => cashbackApi.summary(window),
  });
  const records = useQuery({
    queryKey: ["cashback-records", window.from, window.to, status, page],
    queryFn: () =>
      cashbackApi.records({
        from: window.from,
        to: window.to,
        status: status === "all" ? undefined : status,
        page,
        limit: PAGE_SIZE,
      }),
  });

  const cur = summary.data?.totals;
  const prev = summary.data?.previous;

  const columns: Column<CashbackRecord>[] = [
    { key: "grantedAt", header: t("cb.granted"), render: (r) => formatDateTime(r.grantedAt) },
    {
      key: "trip",
      header: t("cb.trip"),
      render: (r) => (
        <Anchor component={Link} to={`/trips/${r.tripID}`} size="sm">
          {r.trip?.passengerName ?? "—"} → {r.trip?.driverName ?? "—"}
        </Anchor>
      ),
    },
    { key: "rideType", header: t("cb.rideType"), render: (r) => r.rideTypeName ?? "—" },
    { key: "fareBase", header: t("cb.fareBase"), align: "right", render: (r) => formatMoney(r.fareBase) },
    {
      key: "passenger",
      header: t("cb.passenger"),
      align: "right",
      render: (r) => `${formatMoney(r.passengerAmount)} (${r.passengerPercentage}%)`,
    },
    {
      key: "driver",
      header: t("cb.driver"),
      align: "right",
      render: (r) => (
        <>
          {formatMoney(r.driverAmount)} ({r.driverPercentage}%)
          {r.driverCommissionOffset > 0 && (
            <Text size="xs" c="dimmed">{t("cb.offsetShort", { amount: formatMoney(r.driverCommissionOffset) })}</Text>
          )}
        </>
      ),
    },
    {
      key: "status",
      header: t("fin.status"),
      render: (r) =>
        r.status === CashbackStatus.REVERSED ? (
          <Stack gap={0}>
            <Badge color="red" variant="light">{t("cb.reversed")}</Badge>
            {r.reversal && (r.reversal.passengerWrittenOff ?? 0) > 0 && (
              <Text size="xs" c="dimmed">{t("cb.writtenOffShort", { amount: formatMoney(r.reversal.passengerWrittenOff) })}</Text>
            )}
          </Stack>
        ) : (
          <Badge color="green" variant="light">{t("cb.grantedBadge")}</Badge>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) =>
        canManage && r.status === CashbackStatus.GRANTED ? (
          <Button size="xs" variant="subtle" color="red" onClick={() => setReversing(r)}>
            {t("cb.reverse")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Stack>
      {summary.error && <QueryError error={summary.error} onRetry={summary.refetch} />}
      <KpiGroup cols={{ base: 2, md: 3, xl: 6 }}>
        <KpiCard loading={summary.isLoading} label={t("cb.netCost")} icon={IconCoins} value={formatMoney(cur?.net)} current={cur?.net} previous={prev?.net} goodWhen="down" compareLabel={vsPrev} hint={t("cb.netCostHint")} />
        <KpiCard loading={summary.isLoading} label={t("cb.toPassengers")} icon={IconUser} value={formatMoney(cur?.passenger)} current={cur?.passenger} previous={prev?.passenger} compareLabel={vsPrev} />
        <KpiCard loading={summary.isLoading} label={t("cb.toDrivers")} icon={IconSteeringWheel} value={formatMoney(cur?.driver)} current={cur?.driver} previous={prev?.driver} compareLabel={vsPrev} hint={t("cb.toDriversHint", { amount: formatMoney(cur?.driverCommissionOffset) })} />
        <KpiCard loading={summary.isLoading} label={t("cb.shareOfEarnings")} icon={IconPercentage} value={formatPercent(cur?.shareOfPlatformEarning)} current={cur?.shareOfPlatformEarning} previous={prev?.shareOfPlatformEarning} goodWhen="down" compareLabel={vsPrev} hint={t("cb.shareOfEarningsHint")} />
        <KpiCard loading={summary.isLoading} label={t("cb.rides")} icon={IconReceiptRefund} value={formatNumber(cur?.rides)} current={cur?.rides} previous={prev?.rides} compareLabel={vsPrev} />
        <KpiCard loading={summary.isLoading} label={t("cb.writtenOff")} icon={IconReceiptOff} value={formatMoney(cur?.passengerWrittenOff)} hint={t("cb.writtenOffHint", { n: cur?.reversedRides ?? 0 })} />
      </KpiGroup>

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Card padding="lg" h="100%">
            <Text fw={650} mb="md">{t("cb.overTime")}</Text>
            {summary.isLoading || !summary.data ? (
              <Skeleton h={260} />
            ) : summary.data.series.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">{t("common.noData")}</Text>
            ) : (
              <BarChart
                h={260}
                data={summary.data.series.map((s) => ({ bucket: s.bucket, passenger: s.passenger, driver: s.driver }))}
                dataKey="bucket"
                type="stacked"
                series={[
                  { name: "passenger", label: t("cb.passenger"), color: "var(--viz-cat-2)" },
                  { name: "driver", label: t("cb.driver"), color: "var(--accent-driver-solid)" },
                ]}
                valueFormatter={(v) => formatCompact(v)}
                withLegend
                gridAxis="y"
                tickLine="none"
                maxBarWidth={36}
              />
            )}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Card padding="lg" h="100%">
            <Text fw={650} mb="md">{t("fin.byRideType")}</Text>
            {!summary.data ? <Skeleton h={200} /> : <ByRideTypeTable rows={summary.data.byRideType} />}
          </Card>
        </Grid.Col>
      </Grid>

      <Card padding="lg">
        <Group justify="space-between" mb="md">
          <Text fw={650}>{t("cb.records")}</Text>
          <SegmentedControl
            size="xs"
            value={status}
            onChange={(v) => {
              setStatus(v as typeof status);
              setPage(1);
            }}
            data={[
              { value: "all", label: t("cb.all") },
              { value: CashbackStatus.GRANTED, label: t("cb.grantedBadge") },
              { value: CashbackStatus.REVERSED, label: t("cb.reversed") },
            ]}
          />
        </Group>
        <DataTable
          columns={columns}
          rows={records.data?.items ?? []}
          rowKey={(r) => r._id}
          loading={records.isLoading}
          error={records.error ? errorMessage(records.error) : null}
          emptyLabel={t("cb.noRecords")}
          page={page}
          total={records.data?.total}
          totalPages={records.data ? Math.max(1, Math.ceil(records.data.total / PAGE_SIZE)) : 1}
          onPageChange={setPage}
        />
      </Card>

      <ReverseModal record={reversing} onClose={() => setReversing(null)} />
    </Stack>
  );
}

function ByRideTypeTable({ rows }: { rows: (CashbackTotals & { key: string; name: string })[] }) {
  const { t } = useI18n();
  return (
    <Table.ScrollContainer minWidth={380}>
      <Table fz="sm" striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th />
            <Table.Th ta="right">{t("cb.rides")}</Table.Th>
            <Table.Th ta="right">{t("cb.passenger")}</Table.Th>
            <Table.Th ta="right">{t("cb.driver")}</Table.Th>
            <Table.Th ta="right">{t("cb.shareShort")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.key}>
              <Table.Td>{r.name}</Table.Td>
              <Table.Td ta="right">{formatNumber(r.rides)}</Table.Td>
              <Table.Td ta="right">{formatMoney(r.passenger)}</Table.Td>
              <Table.Td ta="right">{formatMoney(r.driver)}</Table.Td>
              <Table.Td ta="right">{formatPercent(r.shareOfPlatformEarning)}</Table.Td>
            </Table.Tr>
          ))}
          {rows.length === 0 && (
            <Table.Tr><Table.Td colSpan={5} c="dimmed" ta="center">{t("common.noData")}</Table.Td></Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** Reverse one ride's cashback, with a reason on record (spec §6). */
export function ReverseModal({ record, onClose }: { record: Pick<CashbackRecord, "tripID" | "passengerAmount" | "driverAmount"> | null; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");

  const reverse = useMutation({
    mutationFn: () => cashbackApi.reverse(record!.tripID, reason.trim()),
    onSuccess: (r) => {
      notifications.show({
        color: "green",
        message:
          r.passengerWrittenOff > 0
            ? t("cb.reversedWithWriteOff", { amount: formatMoney(r.passengerWrittenOff) })
            : t("cb.reversedOk"),
      });
      setReason("");
      onClose();
      qc.invalidateQueries({ queryKey: ["cashback-records"] });
      qc.invalidateQueries({ queryKey: ["cashback-summary"] });
      qc.invalidateQueries({ queryKey: ["cashback-trip", record?.tripID] });
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });

  return (
    <Modal opened={record !== null} onClose={onClose} title={t("cb.reverseTitle")}>
      {record && (
        <Stack>
          <Text size="sm">
            {t("cb.reverseBody", {
              passenger: formatMoney(record.passengerAmount),
              driver: formatMoney(record.driverAmount),
            })}
          </Text>
          <Text size="xs" c="dimmed">{t("cb.reverseRules")}</Text>
          <Textarea
            label={t("cb.reason")}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={2}
            maxLength={300}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>{t("cb.cancel")}</Button>
            <Button color="red" onClick={() => reverse.mutate()} loading={reverse.isPending} disabled={reason.trim().length < 3}>
              {t("cb.reverse")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

/** Fraud review: passenger–driver pairs with many short rides together (spec §8). */
function RepeatPairs({ window }: { window: Window }) {
  const { t } = useI18n();
  const [minRides, setMinRides] = useState(5);
  const [maxKm, setMaxKm] = useState(2);

  const q = useQuery({
    queryKey: ["cashback-pairs", window.from, window.to, minRides, maxKm],
    queryFn: () => cashbackApi.repeatPairs({ ...window, minRides, maxDistanceKm: maxKm }),
  });

  return (
    <Card padding="lg">
      <Text fw={650} mb={4}>{t("cb.pairsTitle")}</Text>
      <Text size="xs" c="dimmed" mb="md">{t("cb.pairsHelp")}</Text>
      <Group mb="md">
        <NumberInput label={t("cb.minRides")} value={minRides} onChange={(v) => setMinRides(Number(v) || 2)} min={2} max={1000} w={140} size="sm" />
        <NumberInput label={t("cb.maxKm")} value={maxKm} onChange={(v) => setMaxKm(Number(v) || 0)} min={0} max={100} decimalScale={1} suffix=" km" w={140} size="sm" />
      </Group>
      {q.error && <QueryError error={q.error} onRetry={q.refetch} />}
      {q.isLoading || !q.data ? (
        <Skeleton h={200} />
      ) : (
        <Table.ScrollContainer minWidth={720}>
          <Table fz="sm" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("cb.passenger")}</Table.Th>
                <Table.Th>{t("cb.driver")}</Table.Th>
                <Table.Th ta="right">{t("cb.rides")}</Table.Th>
                <Table.Th ta="right">{t("cb.distance")}</Table.Th>
                <Table.Th ta="right">{t("cb.cashbackPaid")}</Table.Th>
                <Table.Th>{t("cb.period")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {q.data.pairs.map((p) => (
                <Table.Tr key={`${p.passengerId}-${p.driverId}`}>
                  <Table.Td>
                    <Anchor component={Link} to={`/users/${p.passengerId}`} size="sm">{p.passengerName ?? p.passengerId}</Anchor>
                    {p.passengerPhone && <Text size="xs" c="dimmed">{p.passengerPhone}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Anchor component={Link} to={`/drivers/${p.driverId}`} size="sm">{p.driverName ?? p.driverId}</Anchor>
                    {p.driverPhone && <Text size="xs" c="dimmed">{p.driverPhone}</Text>}
                  </Table.Td>
                  <Table.Td ta="right">{formatNumber(p.rides)}</Table.Td>
                  <Table.Td ta="right">{(p.distanceKm ?? 0).toFixed(1)} km</Table.Td>
                  <Table.Td ta="right">
                    {formatMoney(p.cashbackPassenger + p.cashbackDriver)}
                    <Text size="xs" c="dimmed">{t("cb.grantedOn", { n: p.grantedRides })}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{formatDateTime(p.firstAt)}</Text>
                    <Text size="xs">{formatDateTime(p.lastAt)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {q.data.pairs.length === 0 && (
                <Table.Tr><Table.Td colSpan={6} c="dimmed" ta="center">{t("cb.noPairs")}</Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Text size="xs" c="dimmed" mt="sm">{t("cb.pairsNext")}</Text>
    </Card>
  );
}
